import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Button,
  ButtonColor,
  ButtonState,
} from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { Spinner } from '@delmonicos/shared-design-system-react/src/components/spinner/spinner';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import {
  Text,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { GatewayChargePointDiagnosticsModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-point-diagnostics/models/outputs/gateway-charge-point-diagnostics.model';
import { formatDate } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import { useTranslation } from 'react-i18next';

import i18n from '@app/core/locale/i18n';

import * as css from './charge-point-diagnostic-list.module.scss';

export interface ChargePointDiagnosticListViewProps {
  diagnostics?: GatewayChargePointDiagnosticsModel[];
  diagnosticRequestPending: boolean;
  diagnosticRequestStale: boolean;
  handleDownloadDiagnostic: (
    diagnostic: GatewayChargePointDiagnosticsModel,
  ) => void;
  handleGetDiagnostic: () => void;
}

const DiagnosticHeadlineFragment = ({
  diagnostics,
  diagnosticRequestPending,
  diagnosticRequestStale,
  handleDownloadDiagnostic,
}: Pick<
  ChargePointDiagnosticListViewProps,
  | 'diagnostics'
  | 'diagnosticRequestPending'
  | 'diagnosticRequestStale'
  | 'handleDownloadDiagnostic'
>): React.JSX.Element | null => {
  const { t } = useTranslation();

  if (!diagnostics) {
    return null;
  }

  const diagnostic = diagnostics.at(0);

  if (!diagnostic) {
    return <Text>{t('$.charge-point-diagnostic-list.not-uploaded')}</Text>;
  }

  if (!diagnostic.uploadedAt) {
    if (!diagnosticRequestPending && diagnosticRequestStale) {
      return <Text>{t('$.charge-point-diagnostic-list.not-uploaded')}</Text>;
    }

    return (
      <Tag className="bg-surface-secondary" flex={1} gap={GapEnum.XS}>
        <Spinner />
        <Text bold>{t('$.charge-point-diagnostic-create.pending')}</Text>
      </Tag>
    );
  }

  return (
    <Stack
      alignItems="center"
      direction="row"
      flex={1}
      justifyContent="space-between">
      <Text>{formatDate(diagnostic.uploadedAt, i18n)}</Text>
      <ButtonIcon
        color={ButtonColor.Primary}
        icon="download-01"
        onClick={() => handleDownloadDiagnostic(diagnostic)}
      />
    </Stack>
  );
};

const DiagnosticListFragment = ({
  diagnostics,
  handleDownloadDiagnostic,
}: Pick<
  ChargePointDiagnosticListViewProps,
  'diagnostics' | 'handleDownloadDiagnostic'
>): React.JSX.Element => {
  const { t } = useTranslation();

  if (!diagnostics || diagnostics.length === 0) {
    return (
      <Stack direction="row" justifyContent="center" alignItems="center">
        <Text>{t('$.charge-point-diagnostic-list.empty')}</Text>
      </Stack>
    );
  }

  return (
    <>
      {diagnostics.map((diagnostic) => (
        <Stack key={diagnostic.id} justifyContent="space-between">
          <Stack
            alignItems="center"
            direction="row"
            gap={GapEnum.M}
            justifyContent="space-between">
            <Icon name="file-search-02" />
            {diagnostic.uploadedAt
              ? formatDate(diagnostic.uploadedAt, i18n)
              : t('$.charge-point-diagnostic-list.not-uploaded')}
          </Stack>
          <ButtonIcon
            color={ButtonColor.Secondary}
            disabled={!diagnostic.uploadedAt}
            icon="download-01"
            onClick={() => handleDownloadDiagnostic(diagnostic)}
            state={
              diagnostic.uploadedAt ? ButtonState.Default : ButtonState.Disabled
            }
          />
        </Stack>
      ))}
    </>
  );
};

const DiagnosticGetFragment = ({
  diagnosticRequestPending,
  diagnosticRequestStale,
  handleGetDiagnostic,
}: Pick<
  ChargePointDiagnosticListViewProps,
  'diagnosticRequestPending' | 'diagnosticRequestStale' | 'handleGetDiagnostic'
>): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <Button
      state={
        diagnosticRequestPending || !diagnosticRequestStale
          ? ButtonState.Disabled
          : ButtonState.Default
      }
      color={ButtonColor.Primary}
      onClick={() => handleGetDiagnostic()}>
      <Text>{t('$.charge-point-diagnostic-create.action')}</Text>
    </Button>
  );
};

export const ChargePointDiagnosticListView = (
  props: ChargePointDiagnosticListViewProps,
): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className={css.diagnosticListContainer}>
      <Stack direction="column" gap={GapEnum.L}>
        <Stack direction="row" alignItems="center" gap={GapEnum.M}>
          <Text bold fontSize={TextFontSize.Large}>
            {t('$.charge-point-diagnostic-list.last')}
          </Text>
          <DiagnosticHeadlineFragment {...props} />
        </Stack>
        <Card className={css.diagnosticListCard}>
          <Stack direction="column" gap={GapEnum.M}>
            <DiagnosticListFragment {...props} />
          </Stack>
        </Card>
        <DiagnosticGetFragment {...props} />
      </Stack>
    </div>
  );
};
